import * as Sentry from '@sentry/react';
import type { Span } from '@sentry/react';
import { useEffect, useRef } from 'react';
import { type OperationKey, useMe } from '@vrplatform/data';
import type { OperationTrackerStore } from './operationTracker.store';

/**
 * Times every async operation this session starts, from the API's
 * acknowledgement until the operation reaches a final status. Statement
 * generation and journal refreshes run as such operations, after the write
 * span of the request that started them has ended. Operations recovered from
 * the team summary were started elsewhere and are not timed.
 */
export function useOperationSpans(store: OperationTrackerStore) {
  const spans = useRef(new Map<OperationKey, Span>());
  const { type: userType } = useMe();

  useEffect(
    () =>
      store.subscribe((state) => {
        for (const operation of Object.values(state.operations)) {
          const span = spans.current.get(operation.key);
          const isRunning =
            operation.status === 'queued' || operation.status === 'running';

          if (!span && isRunning && operation.source === 'acknowledgement') {
            spans.current.set(
              operation.key,
              Sentry.startInactiveSpan({
                name: `operation.${operation.operation.type}`,
                op: 'ui.operation',
                attributes: {
                  'team.id': operation.reference.requesterTeamId,
                  portal: userType === 'owner' ? 'owner' : 'pm',
                  'operation.type': operation.operation.type,
                },
              })
            );
          } else if (span && !isRunning) {
            if (operation.status !== 'completed') {
              span.setStatus({ code: 2, message: operation.status });
            }
            span.end();
            spans.current.delete(operation.key);
          }
        }
      }),
    [store, userType]
  );
}
